import { useEffect, useRef, useState, ReactNode } from "react";

interface LazySectionProps {
  children: ReactNode;
  placeholder?: ReactNode;
  rootMargin?: string;
}

export const LazySection = ({ 
  children, 
  placeholder = <div className="min-h-[200px]" />,
  rootMargin = "400px" 
}: LazySectionProps) => {
  // Start visible during SSR/SSG so content is in the static HTML
  const [isVisible, setIsVisible] = useState(typeof window === 'undefined');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // If already visible (SSG hydration), skip observer
    if (isVisible) {
      console.log('[LazySection] Already visible, skipping observer');
      return;
    }

    console.log('[LazySection] Setting up IntersectionObserver, rootMargin:', rootMargin);

    const observer = new IntersectionObserver(
      ([entry]) => {
        console.log('[LazySection] Intersection callback:', {
          isIntersecting: entry.isIntersecting,
          intersectionRatio: entry.intersectionRatio,
          boundingClientRect: entry.boundingClientRect,
        });
        if (entry.isIntersecting) {
          console.log('[LazySection] Now visible via observer');
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    if (ref.current) {
      console.log('[LazySection] Observing element:', ref.current.getBoundingClientRect());
      observer.observe(ref.current);
    } else {
      console.warn('[LazySection] ref.current is null, cannot observe');
    }

    // Fallback: if section hasn't loaded after 3s, force it visible
    const timeout = setTimeout(() => {
      console.log('[LazySection] Fallback timeout triggered after 3s');
      setIsVisible(true);
      observer.disconnect();
    }, 3000);

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [rootMargin, isVisible]);

  return (
    <div ref={ref}>
      {isVisible ? children : placeholder}
    </div>
  );
};
